import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from 'src/app/private/services/api.service';
import { WebLiveChatService } from 'src/app/private/services/web-live-chat.service';
import { environment } from 'src/environments/environment';

/** @deprecated Use WebChatStartResult from web-live-chat.service */
export interface ConnectChatStartResponse {
  sessionId?: string;
  session_id?: string;
  websocketUrl?: string;
  ws_token?: string;
  status?: string;
  lead_id?: number;
  customer_id?: string;
  message?: string;
  code?: string;
}

@Injectable({ providedIn: 'root' })
export class SupportChatService {
  constructor(
    private api: ApiService,
    private webChat: WebLiveChatService
  ) {}

  getLeadIdByCustomerId(payload: {
    client_id?: string | number;
    embed_token?: string;
    customer_id: string;
  }): Observable<{
    lead_id?: number;
    customer_id?: string;
    contact_name?: string;
    message?: string;
  }> {
    return this.api.post(environment.getLeadIdByCustomerId, payload);
  }

  createLiveChatLead(payload: {
    embed_token: string;
    contact_name?: string;
    customer_name?: string;
    contact_phone?: string;
    customer_phone?: string;
    contact_email?: string;
    customer_email?: string;
    country_code?: string;
  }): Observable<any> {
    return this.webChat.createWebChatLead(payload);
  }

  startConnectChat(payload: {
    embed_token: string;
    user_id: string | number;
    visitor_id?: string;
    displayName?: string;
    customer_name?: string;
    email?: string;
    customer_email?: string;
    customer_id?: string;
    subject?: string;
    chat_subject?: string;
    lead_id?: number;
    customer_phone?: string;
  }): Observable<ConnectChatStartResponse> {
    return this.webChat
      .startWebChat({
        embed_token: payload.embed_token,
        visitor_id: String(payload.visitor_id ?? payload.user_id),
        customer_name: payload.customer_name ?? payload.displayName ?? 'Visitor',
        subject: payload.subject ?? payload.chat_subject ?? 'Support',
        lead_id: payload.lead_id,
        customer_id: payload.customer_id,
        customer_email: payload.customer_email ?? payload.email,
        customer_phone: payload.customer_phone,
      })
      .pipe(
        map((res) => ({
          sessionId: res.session_id,
          session_id: res.session_id,
          websocketUrl: res.websocketUrl,
          ws_token: res.ws_token,
          status: res.status,
        }))
      );
  }

  sendConnectChatMessage(payload: {
    embed_token?: string;
    user_id?: string | number;
    visitor_id?: string;
    sessionId?: string;
    session_id?: string;
    content: string;
    client_id?: string | number;
    agent_id?: string | number;
    agent_name?: string;
    attachments?: Array<{ key: string; name: string; mime?: string; size?: number }>;
  }): Observable<any> {
    return this.webChat.sendWebChatMessage({
      session_id: String(payload.session_id ?? payload.sessionId),
      content: payload.content,
      embed_token: payload.embed_token,
      visitor_id: payload.visitor_id ?? (payload.user_id != null ? String(payload.user_id) : undefined),
      client_id: payload.client_id,
      agent_id: payload.agent_id,
      agent_name: payload.agent_name,
      attachments: payload.attachments,
    });
  }

  getWebChatMessages(payload: {
    session_id: string;
    embed_token?: string;
    client_id?: string | number;
    after_ulid?: string;
    limit?: number;
  }): Observable<{ items: any[] }> {
    return this.webChat.getWebChatMessages(payload);
  }

  endConnectChat(payload: {
    embed_token?: string;
    user_id?: string | number;
    visitor_id?: string;
    sessionId?: string;
    session_id?: string;
    client_id?: string | number;
    agent_id?: string | number;
    agent_name?: string;
  }): Observable<any> {
    return this.webChat.endWebChat({
      session_id: String(payload.session_id ?? payload.sessionId),
      embed_token: payload.embed_token,
      visitor_id: payload.visitor_id ?? (payload.user_id != null ? String(payload.user_id) : undefined),
      client_id: payload.client_id,
      agent_id: payload.agent_id,
      agent_name: payload.agent_name,
    });
  }

  uploadConnectChatAttachment(payload: {
    embed_token?: string;
    user_id?: string | number;
    sessionId?: string;
    session_id?: string;
    fileBase64?: string;
    fileName?: string;
    contentType?: string;
    file?: File;
    client_id?: string | number;
  }): Observable<{ ok?: boolean; name?: string; object_key?: string; attachment?: any }> {
    const session_id = String(payload.session_id ?? payload.sessionId);
    const meta = {
      session_id,
      embed_token: payload.embed_token,
      client_id: payload.client_id,
    };

    if (payload.file) {
      return this.webChat.uploadAttachmentFile(meta, payload.file).pipe(
        map((att) => ({ ok: true, name: att.name, object_key: att.object_key, attachment: att }))
      );
    }

    return new Observable((observer) => {
      if (!payload.fileBase64 || !payload.fileName) {
        observer.error(new Error('file is required'));
        return;
      }
      const mime = payload.contentType || 'application/octet-stream';
      const binary = atob(payload.fileBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const file = new File([bytes], payload.fileName, { type: mime });
      this.webChat.uploadAttachmentFile(meta, file).subscribe({
        next: (att) => {
          observer.next({ ok: true, name: att.name, object_key: att.object_key, attachment: att });
          observer.complete();
        },
        error: (err) => observer.error(err),
      });
    });
  }

  getConnectChatAttachmentUrl(payload: {
    embed_token?: string;
    user_id?: string | number;
    sessionId?: string;
    session_id?: string;
    attachmentId?: string;
    object_key?: string;
    client_id?: string | number;
  }): Observable<{ url?: string }> {
    const object_key = payload.object_key ?? payload.attachmentId;
    return this.webChat
      .getWebChatAttachmentUrl({
        session_id: String(payload.session_id ?? payload.sessionId),
        object_key: String(object_key),
        embed_token: payload.embed_token,
        client_id: payload.client_id,
      })
      .pipe(map((res) => ({ url: res.download_url })));
  }
}
